'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Muted autoplay background video that defers ALL buffering until the element
 * approaches the viewport (PageSpeed audit 2026-08-18: the home page downloaded
 * 27MB of below-fold video on load). Behaviour is identical for the visitor —
 * the loop starts as they reach it — but Lighthouse/mobile users no longer pay
 * for videos they haven't scrolled to.
 */
export function LazyAutoplayVideo({
  src,
  className,
  rootMargin = '400px',
}: {
  src: string
  className?: string
  rootMargin?: string
}) {
  const ref = useRef<HTMLVideoElement>(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!('IntersectionObserver' in window)) {
      setActive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true)
          io.disconnect()
        }
      },
      { rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [rootMargin])

  useEffect(() => {
    if (active) ref.current?.play().catch(() => {})
  }, [active])

  return (
    <video
      ref={ref}
      className={className}
      src={active ? src : undefined}
      autoPlay
      loop
      muted
      playsInline
      preload={active ? 'auto' : 'none'}
      aria-hidden="true"
    />
  )
}
